import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Train, Plane, Clock, MessageCircle, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { useLang } from "../i18n/LanguageContext";
import { SITE } from "../i18n/translations";
import { useBooking } from "../context/BookingContext";

const STEPS = [
  { icon: Train, title: "By Rail", desc: "The nearest railhead is Rampurhat, a short road journey from Tarapith." },
  { icon: Plane, title: "By Air", desc: "Kolkata is the nearest major airport, with onward rail or road travel." },
  { icon: MapPin, title: "Location", desc: "Tarapith, Birbhum district, West Bengal, India — temple & Mahasmasan." },
  { icon: Clock, title: "Timings", desc: "Worship follows a traditional daily schedule; confirm before festival days." },
];

export default function Visit() {
  const { t } = useLang();
  const { openBooking } = useBooking();

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Seo
        title="Visiting Tarapith — Pilgrim's Practical Guide"
        description="How to reach Tarapith, temple timings, etiquette and how to plan a meaningful pilgrimage — plus arranging a special puja or havan during your visit."
        path="/visit"
        keywords={["visiting Tarapith", "how to reach Tarapith", "Tarapith temple timings", "Tarapith pilgrimage"]}
      />
      <Breadcrumbs items={[{ label: t("nav_visit") }]} />

      <div className="max-w-2xl mb-10">
        <span className="text-xs font-serif tracking-[0.25em] uppercase text-tara-gold/90">Pilgrim's Guide</span>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-tara-ink mt-2 mb-3">{t("nav_visit")}</h1>
        <p className="text-tara-muted">A practical guide to reaching and experiencing the sacred landscape of Tarapith with reverence.</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 mb-10">
        {STEPS.map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.07 }} className="glass glass-reflect rounded-2xl p-6 flex gap-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/70 border border-tara-gold/30 shrink-0">
              <s.icon className="h-6 w-6 text-tara-gold" />
            </span>
            <div>
              <h2 className="font-display text-xl font-semibold text-tara-ink mb-1">{s.title}</h2>
              <p className="text-sm text-tara-muted leading-relaxed">{s.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="glass-strong glass-reflect rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-5 mb-8">
        <div>
          <h3 className="font-display text-2xl font-semibold text-tara-ink mb-1">Planning a special puja during your visit?</h3>
          <p className="text-sm text-tara-muted">Arrange it in advance with guidance on timing and procedure.</p>
        </div>
        <button onClick={() => openBooking("maa-tara-puja")} className="aura-pulse whitespace-nowrap flex items-center gap-2 rounded-full bg-gradient-to-r from-tara-gold to-tara-vermilion px-6 py-3.5 font-serif font-semibold text-white hover:brightness-110 transition" data-testid="visit-cta-btn">
          <MessageCircle className="h-5 w-5" /> {t("cta_enquire")}
        </button>
      </div>

      <Link to="/knowledge/visiting-tarapith" className="inline-flex items-center gap-1.5 text-tara-gold font-serif font-semibold hover:gap-2.5 transition-all" data-testid="visit-read-full">
        Read the full guide <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
